import { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LangContext";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Megaphone, Loader2, Youtube, CheckCircle2 } from "lucide-react";

const youtubeId = (url) => { const m = url.match(/(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/ ]{11})/); return m ? m[1] : null; };
const tiktokId = (url) => { const m = url.match(/tiktok\.com\/.*\/video\/(\d+)/); return m ? m[1] : null; };
const platform = (url) => { if (!url) return null; if (url.includes("tiktok.com")) return "tiktok"; if (url.includes("youtube.com") || url.includes("youtu.be")) return "youtube"; return null; };

const PACKAGES = [
  { id: "trial",    name: "تجريبية",   name_en: "Trial",    price: 3,  days: 1,  emoji: "🥉", color: "from-orange-600 to-orange-400", border: "border-orange-500/40", priority: "عادية" },
  { id: "basic",    name: "أساسية",    name_en: "Basic",    price: 7,  days: 3,  emoji: "🥈", color: "from-slate-400 to-slate-200", border: "border-slate-400/40", priority: "متوسطة" },
  { id: "advanced", name: "متقدمة",    name_en: "Advanced", price: 15, days: 7,  emoji: "🥇", color: "from-[#D4AF37] to-yellow-200", border: "border-[#D4AF37]/40", priority: "عالية" },
  { id: "pro",      name: "احترافية",  name_en: "Pro",      price: 30, days: 15, emoji: "💎", color: "from-cyan-400 to-blue-300", border: "border-cyan-400/40", priority: "أعلى ⭐" },
];

const PAY_METHODS = [
  { id: "balance",  label: "من رصيدي مباشرة", icon: "💼" },
  { id: "zaincash", label: "زين كاش", icon: "📱" },
  { id: "super_qi", label: "سوبر كي", icon: "💳" },
  { id: "usdt",     label: "USDT (TRC20)", icon: "💎" },
  { id: "trx",      label: "TRX (TRC20)", icon: "⚡" },
];

export default function PromoAds() {
  const { user } = useAuth();
  const { lang } = useLang();
  const [step, setStep] = useState(1); // 1=pick package, 2=URL+pay
  const [pkg, setPkg] = useState(null);
  const [url, setUrl] = useState("");
  const [payment, setPayment] = useState("balance");
  const [walletInfo, setWalletInfo] = useState(null);
  const [mine, setMine] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    const [m, w] = await Promise.all([api.get("/promo/mine"), api.get("/wallet")]);
    setMine(m.data); setWalletInfo(w.data);
  };
  useEffect(() => { load(); }, []);

  const payAddress = () => {
    if (payment === "zaincash") return walletInfo?.platform_zaincash_number;
    if (payment === "super_qi") return walletInfo?.platform_superqi_number;
    if (payment === "trx" || payment === "usdt") return walletInfo?.okx_trx_address;
    return null;
  };

  const submit = async () => {
    if (!platform(url)) { toast.error("رابط YouTube أو TikTok فقط"); return; }
    if (!pkg) { toast.error("اختر باقة"); return; }
    setLoading(true);
    try {
      await api.post("/promo/submit", { video_url: url, package: pkg.id, payment_method: payment });
      toast.success(payment === "balance" ? "✓ تم الخصم من الرصيد — ينتظر الأدمن" : "✓ تم تسجيل الطلب. الأدمن سيتحقق من الدفع");
      setStep(1); setPkg(null); setUrl("");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };

  const statusBadge = (s) => {
    const m = {
      pending: "bg-[#FF9500]/15 text-[#FF9500]",
      awaiting_payment: "bg-[#FF9500]/15 text-[#FF9500]",
      active: "bg-[#00E676]/15 text-[#00E676]",
      rejected: "bg-red-500/15 text-red-400",
    };
    return <span className={`text-xs px-2 py-0.5 rounded ${m[s] || "bg-[#1A2D4D] text-[#8FA3BD]"}`}>{s}</span>;
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
      <h1 className="text-3xl sm:text-4xl font-bold text-cyan-gradient mb-2 flex items-center gap-3">
        <Megaphone className="w-7 h-7" /> {lang === "ar" ? "ترويج رابط" : "Promote Link"}
      </h1>
      <p className="text-sm text-[#8FA3BD] mb-6">روّج رابط YouTube أو TikTok بباقة ثابتة. كل المبلغ يذهب للمنصة.</p>

      {step === 1 && (
        <>
          <h3 className="text-lg font-semibold mb-4">اختر باقتك</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {PACKAGES.map((p) => (
              <button key={p.id} onClick={() => { setPkg(p); setStep(2); }} data-testid={`pkg-${p.id}`}
                className={`bento-card p-5 text-start transition hover:scale-105 ${p.border} hover:shadow-xl`}>
                <div className="text-3xl mb-2">{p.emoji}</div>
                <div className="text-lg font-bold">{lang === "ar" ? p.name : p.name_en}</div>
                <div className={`text-3xl font-bold bg-gradient-to-r ${p.color} bg-clip-text text-transparent mt-2`}>${p.price}</div>
                <div className="text-xs text-[#8FA3BD] mt-2">المدة: <b className="text-white">{p.days} {p.days === 1 ? "يوم" : "أيام"}</b></div>
                <div className="text-xs text-[#8FA3BD]">الأولوية: <b className="text-[#00E5FF]">{p.priority}</b></div>
              </button>
            ))}
          </div>
        </>
      )}

      {step === 2 && pkg && (
        <div className="bento-card p-6 mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Button variant="outline" className="border-[#1A2D4D]" onClick={() => setStep(1)} data-testid="back-pkg">← رجوع</Button>
            <div>
              <div className="text-xs text-[#8FA3BD]">الباقة المختارة</div>
              <div className="font-bold">{pkg.emoji} {lang === "ar" ? pkg.name : pkg.name_en} — ${pkg.price} / {pkg.days} {pkg.days === 1 ? "يوم" : "أيام"}</div>
            </div>
          </div>

          <Label className="text-xs text-[#8FA3BD] flex items-center gap-1.5 mb-1.5"><Youtube className="w-3 h-3" /> رابط YouTube أو TikTok</Label>
          <Input value={url} onChange={(e) => setUrl(e.target.value)}
            placeholder="https://youtube.com/... أو https://tiktok.com/..."
            className="bg-[#050E1A] border-[#1A2D4D]" data-testid="promo-url-input" />

          {(youtubeId(url) || tiktokId(url)) && (
            <div className="mt-4 max-w-md mx-auto aspect-video rounded-lg overflow-hidden border border-[#1A2D4D]">
              {youtubeId(url) && <iframe src={`https://www.youtube.com/embed/${youtubeId(url)}`} className="w-full h-full" frameBorder="0" allowFullScreen title="preview" />}
              {tiktokId(url) && <iframe src={`https://www.tiktok.com/embed/v2/${tiktokId(url)}`} className="w-full h-full" frameBorder="0" allowFullScreen title="preview" />}
            </div>
          )}

          <Label className="text-xs text-[#8FA3BD] mt-5 mb-2 block">طريقة الدفع</Label>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {PAY_METHODS.map((m) => (
              <button key={m.id} onClick={() => setPayment(m.id)} data-testid={`promo-pay-${m.id}`}
                className={`p-3 rounded-lg border text-sm ${payment === m.id ? "border-[#00E5FF] bg-[#00E5FF]/10 text-[#00E5FF]" : "border-[#1A2D4D] text-[#8FA3BD]"}`}>
                {m.icon} {m.label}
              </button>
            ))}
          </div>

          {payment !== "balance" && payAddress() && (
            <div className="mt-4 p-4 rounded-lg bg-[#050E1A] border border-[#FF9500]/40">
              <div className="text-sm font-bold text-[#FF9500] mb-2">📤 حوّل ${pkg.price} إلى:</div>
              <code className="text-[#00E5FF] font-bold break-all">{payAddress()}</code>
              <div className="text-[10px] text-[#FF9500] mt-2">⚠ بعد الإرسال اضغط "تم الدفع" — الأدمن سيتحقق من المبلغ ويفعّل الباقة.</div>
            </div>
          )}

          <Button onClick={submit} disabled={loading || !url} className="btn-cyan w-full mt-6" data-testid="promo-confirm-pay">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (payment === "balance" ? `ادفع $${pkg.price} من الرصيد` : "تم الدفع — أرسل للمراجعة")}
          </Button>
        </div>
      )}

      <h2 className="text-lg font-semibold mb-4">إعلاناتك السابقة</h2>
      <div className="space-y-3">
        {mine.map((p) => {
          const now = Date.now();
          const endsAt = p.ends_at ? new Date(p.ends_at).getTime() : null;
          const remainingMs = endsAt ? Math.max(0, endsAt - now) : 0;
          const days = Math.floor(remainingMs / 86400000);
          const hours = Math.floor((remainingMs % 86400000) / 3600000);
          const isActive = p.status === "active" && remainingMs > 0;
          return (
            <div key={p.promo_id} className="bento-card p-4 flex items-center gap-4" data-testid={`promo-mine-${p.promo_id}`}>
              <div className="w-24 aspect-video rounded bg-[#050E1A] flex-shrink-0 overflow-hidden">
                {youtubeId(p.video_url) ? <img src={`https://i.ytimg.com/vi/${youtubeId(p.video_url)}/mqdefault.jpg`} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-[#FF1493] text-xs">TikTok</div>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">{p.video_url}</div>
                <div className="text-xs text-[#8FA3BD] mt-1">{p.package || "custom"} • ${p.budget} • {p.days} يوم</div>
                <div className="text-[10px] text-[#8FA3BD] mt-0.5">رقم الطلب: <code className="text-[#00E5FF]">{p.promo_id}</code></div>
                {isActive && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#00E676]/15 text-[#00E676] font-bold" data-testid={`promo-countdown-${p.promo_id}`}>
                      ⏱ {days}d {hours}h {lang === "ar" ? "متبقي" : "remaining"}
                    </span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#00E5FF]/15 text-[#00E5FF] font-bold" data-testid={`promo-views-${p.promo_id}`}>
                      👁 {p.views_received || 0} {lang === "ar" ? "مشاهدة" : "views"}
                    </span>
                  </div>
                )}
                {!isActive && p.status !== "active" && (
                  <div className="text-[10px] text-[#8FA3BD] mt-1">{lang === "ar" ? "مشاهدات" : "Views"}: {p.views_received || 0}</div>
                )}
                {p.admin_note && <div className="text-xs text-red-400 mt-1">{p.admin_note}</div>}
              </div>
              {statusBadge(p.status)}
            </div>
          );
        })}
        {mine.length === 0 && <div className="text-center text-[#8FA3BD] py-10">لا توجد إعلانات بعد</div>}
      </div>
    </div>
  );
}
